import { AiOutlineLock } from 'react-icons/ai';
import { FiCheckSquare, FiEdit } from 'react-icons/fi';

import { colors } from '@brand/theme/foundations';
import { Draft, PayloadAction } from '@reduxjs/toolkit';
import formatters from 'formatters';
import { EInterventionStatus } from 'models/Intervention';
import { IInterventionTimeline } from 'models/InterventionDetails';
import IInterventionStatusDateChanged from 'models/InterventionStatusChangedDate';

import { IInterventionData } from '../../initialState';

interface ISetTimelineRequestAction {
  data: {
    index: number;
    interventionStatusDates: IInterventionStatusDateChanged[];
    previousNonWhiteStatus: EInterventionStatus | null;
    timeline: IInterventionTimeline[];
    whiteInterventionStatus?: IInterventionTimeline;
    currentInterventionStatus: EInterventionStatus;
  };
}

function setTimeline(
  draft: Draft<IInterventionData>,
  action: PayloadAction<ISetTimelineRequestAction>,
) {
  draft.getDetails.activeTimelineActionIndex = action.payload.data.index;
  draft.getDetails.timeline = action.payload.data.timeline.map(
    (timelineAction, index) => {
      const isPastAction = index < action.payload.data.index;
      const isCurrentAction = index === action.payload.data.index;

      const currentInterventionStatus =
        action.payload.data.currentInterventionStatus;

      const stylizedAvailableTraderActions: IInterventionTimeline = {
        ...timelineAction,
        page: undefined,
        dynamicItems: {
          color: colors.gray[300],
          icon: AiOutlineLock,
          measure: 6,
          date: '/',
        },
      };

      const canEditFirstVisitDateInPastAction =
        currentInterventionStatus === EInterventionStatus.WaitingForQuote ||
        currentInterventionStatus === EInterventionStatus.InspectionPlanned;

      const canEditDateOfRepairInPastAction =
        currentInterventionStatus ===
          EInterventionStatus.RepairInterventionPlanned ||
        currentInterventionStatus === EInterventionStatus.WaitingForInvoice;

      const needsJaimyAction =
        timelineAction.value === EInterventionStatus.ProcessInvoice ||
        timelineAction.value === EInterventionStatus.QuoteEvaluation ||
        timelineAction.value === EInterventionStatus.InspectionPlanned ||
        timelineAction.value === EInterventionStatus.ProcessLeakDetectionReport;

      // Timeline Items before the current active item
      if (isPastAction) {
        const timelineActionIsDateInspectionRequired =
          timelineAction.value === EInterventionStatus.DateInspectionRequired;

        const timelineActionIsInspectionPlanned =
          timelineAction.value === EInterventionStatus.InspectionPlanned;

        const timelineActionIsQuoteEvaluation =
          timelineAction.value === EInterventionStatus.QuoteEvaluation;

        const timelineActionIsLeakDetectionDateRequired =
          timelineAction.value ===
          EInterventionStatus.LeakDetectionDateRequired;

        const timelineActionIsRepairInterventionRequired =
          timelineAction.value ===
          EInterventionStatus.DateRepairInterventionRequired;

        const timelineActionIsRepairInterventionPlanned =
          timelineAction.value ===
          EInterventionStatus.RepairInterventionPlanned;

        const findStatusChangedDate =
          action.payload.data.interventionStatusDates.find(
            interventionStatusDate =>
              interventionStatusDate.oldStatus === timelineAction.value,
          );

        let dynamicDate = findStatusChangedDate?.createdAt
          ? formatters.date(findStatusChangedDate?.createdAt)
          : '/';

        let dynamicIcon = FiCheckSquare;
        let dynamicColor = colors.lightBlue;

        // handle date inspection required
        if (timelineActionIsDateInspectionRequired) {
          dynamicDate = draft.getDetails.values?.firstVisitDateUpdatedAt
            ? formatters.date(draft.getDetails.values?.firstVisitDateUpdatedAt)
            : dynamicDate;
        }

        // handle first visit date in past
        else if (
          timelineActionIsInspectionPlanned &&
          canEditFirstVisitDateInPastAction
        ) {
          dynamicDate = draft.getDetails.values?.firstVisitDate
            ? formatters.date(draft.getDetails.values?.firstVisitDate)
            : dynamicDate;
          stylizedAvailableTraderActions.page = timelineAction.page;
          dynamicIcon = FiEdit;
          dynamicColor = colors.blue[300];
        }

        // handle quote evaluation in past
        else if (timelineActionIsQuoteEvaluation) {
          dynamicDate = draft.getDetails.values?.quoteUpdatedAt || '/';
        }

        // handle date repair intervention required and leak detection date required
        else if (
          timelineActionIsRepairInterventionRequired ||
          timelineActionIsLeakDetectionDateRequired
        ) {
          dynamicDate = draft.getDetails.values?.repairDateUpdatedAt
            ? formatters.date(draft.getDetails.values?.repairDateUpdatedAt)
            : dynamicDate;
          dynamicIcon = FiCheckSquare;

          // handle repair date in past
        } else if (
          timelineActionIsRepairInterventionPlanned &&
          canEditDateOfRepairInPastAction
        ) {
          dynamicDate = draft.getDetails.values?.repairDateUpdatedAt
            ? formatters.date(draft.getDetails.values?.repairDateUpdatedAt)
            : dynamicDate;
          dynamicIcon = FiEdit;
          dynamicColor = colors.blue[300];
          stylizedAvailableTraderActions.page = timelineAction.page;
        }
        stylizedAvailableTraderActions.dynamicItems.date = dynamicDate;
        stylizedAvailableTraderActions.dynamicItems.icon = dynamicIcon;
        stylizedAvailableTraderActions.dynamicItems.color = dynamicColor;

        // Current active timeline item
      } else if (isCurrentAction) {
        stylizedAvailableTraderActions.dynamicItems.color = needsJaimyAction
          ? colors.heading
          : colors.primary.base;
        stylizedAvailableTraderActions.dynamicItems.icon =
          timelineAction.rightIcon;
        stylizedAvailableTraderActions.page = timelineAction.page;
      }

      const needsToFillWithFirstVisitDate =
        timelineAction.value === EInterventionStatus.InspectionPlanned;
      const needsToFillWithRepairDate =
        timelineAction.value ===
          EInterventionStatus.RepairInterventionPlanned ||
        timelineAction.value === EInterventionStatus.LeakDetectionPlanned;

      if (index <= action.payload.data.index) {
        if (needsToFillWithFirstVisitDate) {
          stylizedAvailableTraderActions.dynamicItems.date = draft.getDetails
            .values?.firstVisitDate
            ? formatters.date(draft.getDetails.values?.firstVisitDate)
            : '/';
        } else if (needsToFillWithRepairDate) {
          stylizedAvailableTraderActions.dynamicItems.date = draft.getDetails
            .values?.repairDate
            ? formatters.date(draft.getDetails.values?.repairDate)
            : '/';
        }
      }

      return stylizedAvailableTraderActions;
    },
  );

  // handle white/common intervention status

  const whiteInterventionStatus = action.payload.data.whiteInterventionStatus;
  const previousNonWhiteStatus = action.payload.data.previousNonWhiteStatus;

  // if current status is a white status and previous_non_white_status exists, get the index of the previous status in the timeline
  if (whiteInterventionStatus) {
    const findStatusChangedDate =
      action.payload.data.interventionStatusDates.find(
        interventionStatusDate =>
          interventionStatusDate.newStatus === whiteInterventionStatus.value,
      );

    const whiteInterventionStatusFormatted: IInterventionTimeline = {
      ...whiteInterventionStatus,
      dynamicItems: {
        ...whiteInterventionStatus.dynamicItems,
        date: findStatusChangedDate?.createdAt
          ? formatters.date(findStatusChangedDate?.createdAt)
          : '/',
      },
    };

    if (previousNonWhiteStatus) {
      const previousNonWhiteListIndex = draft.getDetails.timeline.findIndex(
        timelineItem => timelineItem.value === previousNonWhiteStatus,
      );
      draft.getDetails.activeTimelineActionIndex = previousNonWhiteListIndex;

      // insert white status in the timeline in the previousNonWhiteStatus index + 1
      draft.getDetails.timeline.splice(
        previousNonWhiteListIndex + 1,
        0,
        whiteInterventionStatusFormatted,
      );
      // in case of existing white intervention status but no previous non white intervention status
    } else if (!previousNonWhiteStatus) {
      draft.getDetails.timeline.splice(0, 0, whiteInterventionStatusFormatted);
    }
  }
}

export default setTimeline;
